import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { goodsTransfers, type GoodsTransferItem } from '@/api/goodsTransfers';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_from_id: string;
  warehouse_to_id: string;
  comment: string;
  items: GoodsTransferItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  warehouse_from_id: '',
  warehouse_to_id: '',
  comment: '',
  items: [],
};

const newRow = (n: number): GoodsTransferItem => ({
  line_number: n, nomenclature_id: '', quantity: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
const fmtMoney = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsTransferEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по warehouse.transfer.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('warehouse.transfer.manage');
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const docQuery = useQuery({
    queryKey: ['goods-transfer', id],
    queryFn: () => goodsTransfers.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.goods_transfers', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        warehouse_from_id: d.warehouse_from_id,
        warehouse_to_id: d.warehouse_to_id,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id ?? null,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          cost_amount: it.cost_amount ? String(it.cost_amount) : undefined,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalQty = form.items.reduce((s, e) => s + Number(e.quantity || 0), 0);
  const totalCost = form.items.reduce((s, e) => s + Number(e.cost_amount || 0), 0);

  const create = useMutation({
    mutationFn: () => goodsTransfers.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_from_id: form.warehouse_from_id,
      warehouse_to_id: form.warehouse_to_id,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
      navigate(`/goods-transfers/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsTransfers.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_from_id: form.warehouse_from_id,
      warehouse_to_id: form.warehouse_to_id,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-transfer', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти чернетку і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await goodsTransfers.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_from_id: form.warehouse_from_id,
        warehouse_to_id: form.warehouse_to_id,
        comment: form.comment || null,
        items: form.items,
      });
      await goodsTransfers.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
      navigate(`/goods-transfers/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await goodsTransfers.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_from_id: form.warehouse_from_id,
        warehouse_to_id: form.warehouse_to_id,
        comment: form.comment || null,
        items: form.items,
      });
      await goodsTransfers.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-transfer', id] });
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsTransfers.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-transfer', id] });
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsTransfers.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
      navigate('/goods-transfers');
    },
  });

  function setItem(i: number, patch: Partial<GoodsTransferItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має warehouse.transfer.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати переміщення товарів у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-transfers')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове переміщення' : `Переміщення: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/goods-transfers')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          postDisabled={form.items.length === 0}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postPending={create.isPending || createAndPost.isPending || update.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Зі складу</label>
          <select
            value={form.warehouse_from_id}
            onChange={(e) => setForm({ ...form, warehouse_from_id: e.target.value })}
            disabled={isPosted}
          >
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>На склад</label>
          <select
            value={form.warehouse_to_id}
            onChange={(e) => setForm({ ...form, warehouse_to_id: e.target.value })}
            disabled={isPosted}
          >
            <option value="">—</option>
            {warehousesQ.data?.filter((w) => w.id !== form.warehouse_from_id).map((w) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label>Коментар</label>
        <input
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          disabled={isPosted}
        />
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Рядки</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Номенклатура</th>
            <th style={{ padding: '0.5rem', width: 120 }}>Кількість</th>
            {isPosted && <th style={{ padding: '0.5rem', width: 140 }}>Собівартість</th>}
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select
                  value={it.nomenclature_id}
                  onChange={(e) => setItem(i, { nomenclature_id: e.target.value })}
                  disabled={isPosted}
                >
                  <option value="">—</option>
                  {nomList.data?.items.map((n) => (
                    <option key={n.id} value={n.id}>{n.code} — {n.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input
                  type="number" step="0.001" value={it.quantity}
                  onChange={(e) => setItem(i, { quantity: e.target.value })}
                  disabled={isPosted}
                />
              </td>
              {isPosted && (
                <td style={{ padding: '0.5rem', textAlign: 'right', color: '#64748b' }}>
                  {fmtMoney(Number(it.cost_amount ?? 0))}
                </td>
              )}
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button
                    onClick={() => removeRow(i)}
                    className="danger"
                    style={{ padding: '0.3rem 0.6rem' }}
                  >×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={2} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalQty)}</td>
            {isPosted && (
              <td style={{ padding: '0.5rem', textAlign: 'right', color: '#64748b' }}>
                {fmtMoney(totalCost)}
              </td>
            )}
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}
      {error && <div className="error">{error}</div>}
      {isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          Внутрішнє переміщення: бухгалтерські проводки не формуються
          (товар залишається у власності організації, рахунок 281 не змінюється).
          Рухи проходять у регістрах «Товари на складах» і «Партії товарів».
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
